import { useId } from 'react'
import { data, Form, redirect } from 'react-router'
import { HoneypotInputs } from 'remix-utils/honeypot/react'
import { OyButton } from '#app/components/oy/oy-button.tsx'
import { OyDoor } from '#app/components/oy/oy-door.tsx'
import { OyField, OySectionLabel } from '#app/components/oy/oy-field.tsx'
import { daughterFirstName } from '#app/utils/circle-identity.server.ts'
import { checkHoneypot } from '#app/utils/honeypot.server.ts'
import {
	commitKringSession,
	resolveCircleViewer,
} from '#app/utils/kring-session.server.ts'
import { OYKA_SENDER_NAME, smsConfigured } from '#app/utils/sms.server.ts'
import {
	clientIpOf,
	requestLoginCode,
	verifyLoginCode,
} from './login.server.ts'
import { type Route } from './+types/login.ts'

/**
 * ADR-026 scenario B — /week/login (alias /aanmelden, the address on the
 * printed card): name → code by text → cookie. Chromeless and noindex like
 * the week page; the honeypot and the per-name / per-IP counters in
 * login.server.ts guard the paid step; the path's "/login" substring puts
 * it under the server's strongest rate limiter. Same neutral sentence for a
 * known and an unknown name — the page never says who is in the circle.
 *
 * Copy rule (§8 look-alike phishing): Oyka never asks for a code by text
 * and never sends a login link that was not just promised on this screen.
 *
 * Herontwerp P8b: restyled in the design language (OyDoor + oy primitives,
 * ADR-027) — the loader/action contract and every role and string that
 * login.test.ts / login.page.test.tsx pin are unchanged.
 */
export const handle = {
	hideAppChrome: true,
	getSitemapEntries: () => null,
}

export const meta: Route.MetaFunction = () => [
	{ title: 'Inloggen voor het weekoverzicht in Oyka' },
	{ name: 'robots', content: 'noindex, nofollow' },
]

export async function loader({ request }: Route.LoaderArgs) {
	// Already in: straight to the page.
	const viewer = await resolveCircleViewer(request, null, new Date())
	if (viewer.ok) throw redirect('/week/mijn')
	return data({
		daughterName: await daughterFirstName(),
		smsOn: smsConfigured(),
		senderName: OYKA_SENDER_NAME,
		// the contact card exists only with the VMN (the route 404s otherwise)
		contactCard: Boolean(process.env.SPRYNG_VMN),
	})
}

type ActionData =
	| { step: 'name'; error?: string }
	| { step: 'code'; name: string; error?: string }

export async function action({ request }: Route.ActionArgs) {
	const form = await request.formData()
	await checkHoneypot(form)
	const intent = String(form.get('intent') ?? '')
	const name = String(form.get('naam') ?? '')
		.trim()
		.slice(0, 120)
	const now = new Date()
	if (intent === 'request') {
		if (!name) {
			return data<ActionData>(
				{ step: 'name', error: 'Vul je naam in.' },
				{ status: 400 },
			)
		}
		// The outcome is deliberately not shown: a known and an unknown name
		// get the same next screen.
		await requestLoginCode(name, { ip: clientIpOf(request), now })
		return data<ActionData>({ step: 'code', name })
	}
	if (intent === 'verify') {
		const code = String(form.get('code') ?? '')
		const res = await verifyLoginCode(name, code, { now })
		if (!res.ok) {
			const error =
				res.reason === 'wrong'
					? 'Die code klopt niet. Kijk even goed in het sms’je.'
					: res.reason === 'locked'
						? 'Te vaak geprobeerd. Vraag een nieuwe code.'
						: 'Die code is verlopen of niet bekend. Vraag een nieuwe.'
			return data<ActionData>({ step: 'code', name, error }, { status: 400 })
		}
		throw redirect('/week/mijn?ingelogd=1', {
			headers: { 'set-cookie': await commitKringSession(res.tokenId) },
		})
	}
	return data<ActionData>({ step: 'name' }, { status: 400 })
}

export default function WeekLoginPage({
	loaderData,
	actionData,
}: Route.ComponentProps) {
	const naamId = useId()
	const codeId = useId()
	const step = actionData?.step ?? 'name'
	const name = actionData?.step === 'code' ? actionData.name : ''
	const error = actionData?.error
	return (
		<OyDoor title="Weekoverzicht">
			{step === 'name' ? (
				<Form method="post">
					<HoneypotInputs />
					<input type="hidden" name="intent" value="request" />
					<p>
						Hoi! Log in met je naam. Je krijgt dan een code per sms op je eigen
						nummer.
					</p>
					<OySectionLabel as="label" htmlFor={naamId}>
						Je naam, zoals {loaderData.daughterName} je kent
					</OySectionLabel>
					<OyField
						id={naamId}
						name="naam"
						autoComplete="name"
						autoFocus
						required
						maxLength={120}
					/>
					{error ? <p className="err">{error}</p> : null}
					<OyButton type="submit">Stuur mij een code</OyButton>
					{!loaderData.smsOn ? (
						<p>
							Het inloggen per sms staat nog niet aan. Vraag{' '}
							{loaderData.daughterName} om je persoonlijke link.
						</p>
					) : null}
				</Form>
			) : (
				<Form method="post">
					<HoneypotInputs />
					<input type="hidden" name="intent" value="verify" />
					<input type="hidden" name="naam" value={name} />
					<p>
						Als je naam bekend is bij Oyka, krijg je zo een code per sms op je
						eigen nummer. Vul die hieronder in.
					</p>
					<OySectionLabel as="label" htmlFor={codeId}>
						Code uit het sms’je
					</OySectionLabel>
					<OyField
						id={codeId}
						name="code"
						inputMode="numeric"
						autoComplete="one-time-code"
						pattern="[0-9 ]*"
						placeholder="123 456"
						autoFocus
						required
					/>
					{error ? <p className="err">{error}</p> : null}
					<OyButton type="submit">Inloggen</OyButton>
					<p>
						Geen sms gekregen?{' '}
						<a href="/week/login">Probeer het nog eens met je naam.</a>
					</p>
				</Form>
			)}
			<footer>
				Oyka vraagt nooit om je code per sms en stuurt nooit een inloglink die
				je niet net op dit scherm hebt aangevraagd.
				{loaderData.smsOn
					? ` Het nummer waar je code vandaan komt: sla het op als ${loaderData.senderName}, dan zie je altijd wie er schrijft.`
					: null}
				{loaderData.smsOn && loaderData.contactCard ? (
					<>
						{' '}
						<a href="/oyka.vcf">Bewaar Oyka in je contacten</a>
					</>
				) : null}
			</footer>
		</OyDoor>
	)
}
